<template>
	<div class="hotel">
		<div class="hotel-infos__left-container">
			<div class="hotel-infos__name">
				<Teleport :to="isMobile ? '#hotel-info-container' : '.hotel-page-main-content__menu > ul'">
					<HotelFeaturesSocial
						:hotel="hotel"
						:best-offer="bestOffer"
					/>
				</Teleport>
			</div>
			<div class="hotel__details-container">
				<StickyOfferButton
					v-if="!animationInProgress && bestOffer && bestOffer.Price && bestOffer.CurrencyCode"
					:visible="needStickyButton"
				/>
				<Teleport
					:to="offerBoxTeleportAddress"
					defer
					:disabled="isMobile"
				>
					<div
						ref="attributesContainer"
						class="hotel-infos__offer-box"
					>
						<OfferBox
							:all-offers-href="`${urlPathname}${urlSearchParams}#offers`"
							@search-update-click="onSearchUpdateClick"
							@package-switch-click="onUpdatePackage"
							@show-offers-click="(event) => (offerBoxBtnAnalytics(), scrollToAnchorWithDynamicOffset(event))"
						/>
					</div>
				</Teleport>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import OfferBox from '@lmt-rpb/OfferBox/OfferBox.vue';
import {
	scrollToAnchorWithDynamicOffset as scrollToAnchorWithDynamicOffsetOriginal,
	scrollIt,
	scrollToHashWithDynamicOffset,
} from '@utils/utils';
import { EventBus } from '@global-js/event-bus';
import { ref, computed, onMounted, watch } from 'vue';
import type { HotelData } from '@interfaces/offer-list-types/offer-list-typo-hotel-data';
import StickyOfferButton from '@lmt-rpb/StickyOfferButton/StickyOfferButton.vue';
import { OFFER_BOX_TELEPORT } from '@global-js/constants';
import { useStore } from '@/components/common/store';
import type { MergedOfferItemData } from '@/interfaces/offer';
import { useBoundaryObserver } from '@/js/composables/boundaryObserver';
import { useSearchParams } from '../../../../storybook/src/composables/searchParams';
import HotelFeaturesSocial from './internals/HotelFeaturesSocial.vue';
import SelectContentAnalytics from '@/js/modules/analytics/SelectContentAnalytics';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';

interface Props {
	hotel: HotelData;
}

const props = withDefaults(defineProps<Props>(), {});

const store = useStore();

const initialRender = ref(true);

const urlPathname = ref('');

const urlSearchParams = useSearchParams(true);
const { isMobile, isDesktop } = storeToRefs(useBreakpointStore());
const offerBoxTeleportAddress = computed(() =>
	isDesktop.value ? `.${OFFER_BOX_TELEPORT.HOTEL_DESCRIPTION}` : `.${OFFER_BOX_TELEPORT.HOTEL_INFOS}`
);

const attributesContainer = ref<HTMLElement | null>(null);
const animationInProgress = ref(false);
const animationEndCallback = () => {
	animationInProgress.value = false;
};
const { isInBounds: needStickyButton } = useBoundaryObserver({ elementRef: attributesContainer, shouldBeVisible: false });
const scrollToAnchorWithDynamicOffset = (event: MouseEvent): void => {
	animationInProgress.value = true;
	scrollToAnchorWithDynamicOffsetOriginal(event, animationEndCallback);
};

const isPackage = props.hotel.SeasonSettings.TravelType === 'Package';

const bestOffer = computed((): Partial<MergedOfferItemData> => {
	let hotelDataOffer: Partial<MergedOfferItemData> = {};
	if (props.hotel?.PackageOffer || props.hotel?.AccommodationOffer) {
		const offer = isPackage ? props.hotel.PackageOffer : props.hotel.AccommodationOffer;
		hotelDataOffer = {
			BoardType: offer.BoardType,
			Room: {
				Type: offer.Room?.Type,
				Name: offer.Room?.Name,
				View: offer.Room?.View,
			},
			CurrencyCode: offer.CurrencyCode,
			duration: 'BetweenDeparturesDuration' in offer ? offer.BetweenDeparturesDuration : offer.OvernightStays,
			Price: offer.Price,
		};
	}
	const emptyOffer = {
		BoardType: undefined,
		Room: undefined,
		CurrencyCode: undefined,
		duration: undefined,
		Price: undefined,
	};

	if (initialRender.value) {
		return store.state.bestOffer ? store.state.bestOffer : hotelDataOffer;
	}
	return store.state.bestOffer ? store.state.bestOffer : emptyOffer;
});

watch(
	() => store.state.bestOffer,
	() => {
		initialRender.value = false;
	}
);
const offerBoxBtnAnalytics = () => {
	SelectContentAnalytics.selectContent({ cId: 'offerbox_button' });
};

const offerBoxLinkAnalytics = () => {
	SelectContentAnalytics.selectContent({ cId: 'offerbox_link' });
};

const onSearchUpdateClick = () => {
	offerBoxBtnAnalytics();
	scrollToHashWithDynamicOffset('offers');
};

const onUpdatePackage = () => {
	offerBoxLinkAnalytics();
	if (!isDesktop.value) {
		EventBus.$emit('SearchMask:MobileSubmit');
	}
	scrollToHashWithDynamicOffset('offers');
};

onMounted((): void => {
	urlPathname.value = window.location.pathname;
	const urlHash = window.location.href.split('#')[1];
	const targetElement = document.querySelector(`#${urlHash}`) as HTMLElement;
	if (targetElement) {
		setTimeout(() => {
			scrollIt(targetElement, 0, 'linear', 50);
		}, 10);
	}
});
</script>

<style lang="scss" scoped>
$top-margin: 1rem;

.hotel {
	&-infos__name {
		text-align: center;
	}

	&__tab {
		color: inherit;
		text-decoration: none;
		cursor: pointer;

		&:hover,
		&:focus,
		&:active {
			color: inherit;
			text-decoration: none;
			cursor: pointer;
		}
	}

	&__review {
		margin: 1.7rem 0;

		&--no-review {
			display: flex;
			font-size: $font-small-1;
		}

		&-box {
			display: flex;
			align-items: baseline;
			cursor: pointer;
			max-width: 85%;
		}

		&-overall-rate {
			font-weight: $font-weight-semibold;
			margin-right: 0.8rem;
			font-size: $font-small-1;
			color: $color-primary;
		}

		&-total {
			font-size: $font-small-3;
			color: $color-black;
		}
	}

	&__details-container {
		display: flex;
		flex-direction: column;

		@media (max-width: $breakpoint-small - 1px) {
			margin-top: 1.6rem;
		}
	}

	&__map {
		overflow: hidden;
		width: 28rem;
		display: block;
		border-radius: 0.6rem;
	}

	&__title {
		padding: 1rem 0;
		color: $color-text;
		font-family: $font-family-special;
		font-size: inherit;
		font-weight: $font-weight-bold;
	}

	&__details {
		margin-top: $top-margin;
		margin-bottom: 4rem;
	}

	&__map-clickable {
		color: inherit;
		text-decoration: none;
		position: relative;
	}

	&__map-img {
		height: 6rem;
		margin: 0 auto;
		display: block;
	}

	&__map-address {
		display: flex;
		justify-content: space-between;
		position: absolute;
		top: 0.4rem;
		margin: 0.5rem auto;
		padding: 0 1rem 0.25rem 0.25rem;
		width: 24.7rem;
		border-radius: 0.6rem;
		background: $color-white;
		flex-wrap: wrap;
		height: 4rem;
		margin-left: 1.7rem;
	}

	&__map-label {
		font-size: $font-small-3;
		height: 4rem;
		margin-left: 1rem;
		margin-top: 1.2rem;
		overflow: hidden;
		font-weight: $font-weight-regular;
		text-overflow: ellipsis;
		white-space: nowrap;

		&::before {
			content: '';
			display: inline-block;
			width: 1.5rem;
			margin-right: 1rem;
			text-overflow: ellipsis;
		}
	}

	&__map-icon {
		left: 1.6rem;
		top: 0.9rem;
		position: absolute;
		fill: $color-link;
		width: 1.6rem;
		height: 2.3rem;
	}

	&-infos__right-container {
		min-height: 10.4rem;
	}

	@media (min-width: $breakpoint-mobilelarge) {
		&-infos__right-container {
			min-height: 11.2rem;
		}
	}

	@media (min-width: $breakpoint-small) {
		.hotel {
			&__details-container {
				flex-direction: row;
				justify-content: space-between;
			}

			&__details {
				margin-bottom: 0;
			}

			&-infos__right-container {
				width: 35rem;
				margin-top: 1rem;
			}

			&-name__title {
				font-size: $font-large-1;
				flex-direction: row;
			}

			&-name__social-icon {
				width: 2.5rem;
				height: 2.5rem;
			}

			&__review-box {
				max-width: 50%;
			}
		}
	}

	@media (min-width: $breakpoint-large) {
		.hotel-infos__right-container {
			min-height: 13.2rem;
			width: 45rem;
			margin-right: 13rem;
		}

		.hotel__review-overall-rate {
			font-size: $font-large-4;
		}

		.hotel__review-total {
			font-size: $font-medium-2;
		}

		.hotel__review-box {
			max-width: 40%;
		}
	}
}
</style>
